import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { Box, Typography, Paper } from '@mui/material';
import { FONTS } from '../../theme/themeConstants';
import { useAtom, useSetAtom } from 'jotai';
// import { storeAtom } from '../Statefull/ManageRestaurantTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storeAtom } from '../statefull/StoreManagement/ManageRestaurantTable';

const styles = {
  typography: {
    fontWeight: FONTS.weight.regular,
    fontFamily: FONTS.family.primary,
  },
};

type DayType =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

interface DeliveryFeeFormProps {
  field: {
    name: string;
    value: any;
  };
  form: {
    setFieldValue: (field: string, value: any) => void;
    values: any;
  };
  errors?: any;
  touched?: any;
  resetTrigger?: number; // Add reset trigger prop
}

const DeliveryFeeForm: React.FC<DeliveryFeeFormProps> = ({ field, form, resetTrigger }) => {
  const { t } = useTranslation();
  const [storeId] = useAtom<any>(storeAtom);

  const weekDays = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ] as const;

  // --- Add: Sync disabledDays with Formik ---
  const initialDisabledDays: Record<DayType, boolean> = (() => {
    const peakData =
      storeId?.restaurantDetails?.deliveryFee?.deliveryChargeSetting
        ?.peakHourDeliveryFeeStatus?.data;

    if (!peakData) {
      return {
        Sunday: true,
        Monday: true,
        Tuesday: true,
        Wednesday: true,
        Thursday: true,
        Friday: true,
        Saturday: true,
      };
    }

    const disabledDays: Record<DayType, boolean> = {
      Sunday: false,
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
    };

    // If we have store data, check which days are disabled based on missing data
    weekDays.forEach((day) => {
      const dayCapitalized = (day.charAt(0).toUpperCase() +
        day.slice(1)) as DayType;
      const dayData = peakData[day];

      // If day has no data or empty data, mark it as disabled
      if (
        !dayData ||
        dayData.length === 0 ||
        (dayData.length === 1 &&
          (!dayData[0].startTime || dayData[0].startTime === '00:00:00Z') &&
          (!dayData[0].endTime || dayData[0].endTime === '00:00:00Z') &&
          (!dayData[0].amount || dayData[0].amount === 0))
      ) {
        disabledDays[dayCapitalized] = true;
      }
    });

    return disabledDays;
  })();

  // Delivery fee is now always using the platform default and is not configurable by merchants.
  // Keep the internal structure, but lock everything to "default" / "disable" so the backend
  // always receives the default delivery fee configuration.
  const defaultType = 'default';
  const defaultStatus = 'disable';

  // Validation function for Formik
  const validate = (values: any) => {
    const errors: any = {};
    const deliveryFee = values.deliveryFee;
    const setting = deliveryFee.deliveryChargeSetting;
    errors.deliveryFee = { deliveryChargeSetting: {} };
    // Delivery Fee Status
    if (setting.type === 'custom') {
      if (!setting.deliveryFeeStatus.type) {
        errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = { type: 'Required' };
      }
      // Common Delivery Fee
      if (setting.deliveryFeeStatus.type === 'enable') {
        if (!setting.deliveryFeeStatus.commonDeliveryFee.type) {
          errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = {
            ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus,
            commonDeliveryFee: { type: 'Required' },
          };
        }
        if (setting.deliveryFeeStatus.commonDeliveryFee.type === 'enable') {
          const val = setting.deliveryFeeStatus.commonDeliveryFee.commonDeliveryFeeValue;
          if (val === '' || val === null || isNaN(val)) {
            errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = {
              ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus,
              commonDeliveryFee: {
                ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus?.commonDeliveryFee,
                commonDeliveryFeeValue: 'Please enter a fee amount (zero allowed)',
              },
            };
          } else if (Number(val) < 0) {
            errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = {
              ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus,
              commonDeliveryFee: {
                ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus?.commonDeliveryFee,
                commonDeliveryFeeValue: 'Amount must be zero or greater',
              },
            };
          }
        }
        // Fee Per KM
        if (!setting.deliveryFeeStatus.feePerKM.type) {
          errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = {
            ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus,
            feePerKM: { type: 'Required' },
          };
        }
        if (setting.deliveryFeeStatus.feePerKM.type === 'enable') {
          const val = setting.deliveryFeeStatus.feePerKM.feePerKMValue;
          if (val === '' || val === null || isNaN(val)) {
            errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = {
              ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus,
              feePerKM: {
                ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus?.feePerKM,
                feePerKMValue: 'Please enter a fee amount (zero allowed)',
              },
            };
          } else if (Number(val) < 0) {
            errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus = {
              ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus,
              feePerKM: {
                ...errors.deliveryFee.deliveryChargeSetting.deliveryFeeStatus?.feePerKM,
                feePerKMValue: 'Amount must be zero or greater',
              },
            };
          }
        }
      }
      // Peak Hour Delivery Fee
      if (!setting.peakHourDeliveryFeeStatus.type) {
        errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus = { type: 'Required' };
      }
      if (setting.peakHourDeliveryFeeStatus.type === 'enable') {
        // Validate each day
        const data = setting.peakHourDeliveryFeeStatus.data;
        const weekDays = [
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
        ];
        errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus = { data: {} };
        weekDays.forEach((day) => {
          const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1) as DayType;
          if (values.disabledDays && values.disabledDays[dayCapitalized]) {
            if (errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day]) {
              delete errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day];
            }
            return;
          }
        
          const hours = data[day] || [];
        
          if (hours.length === 0) {
            if (!errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day]) {
              errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day] = [];
            }
            errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day][0] = {
              ...(errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day]?.[0] || {}),
              general: 'Please configure at least one peak hour or disable the day.',
            };
            return;
          }
        
          hours.forEach((hour: any, idx: number) => {
            let hourError = {} as any;
            // Only error if BOTH are zero
            const isStartZero = !hour.startTime || hour.startTime === '00:00:00Z';
            const isEndZero = !hour.endTime || hour.endTime === '00:00:00Z';
            if (isStartZero && isEndZero) {
              hourError.startTime = 'Start and end time cannot both be 00:00';
              hourError.endTime = 'Start and end time cannot both be 00:00';
            }
            if (hour.amount === '' || hour.amount === null || isNaN(hour.amount)) {
              hourError.amount = 'Please enter a valid amount (zero allowed)';
            } else if (Number(hour.amount) < 0) {
              hourError.amount = 'Amount must be zero or greater';
            }
            if (Object.keys(hourError).length > 0) {
              if (!errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day]) {
                errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day] = [];
              }
              errors.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.data[day][idx] = hourError;
            }
          });
        });
        
      }
    }
    // Clean up empty error objects
    if (Object.keys(errors.deliveryFee.deliveryChargeSetting).length === 0) {
      delete errors.deliveryFee.deliveryChargeSetting;
    }
    if (Object.keys(errors.deliveryFee).length === 0) {
      delete errors.deliveryFee;
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      // Always use platform default delivery fee configuration
      deliveryFee: {
        deliveryChargeSetting: {
          type: defaultType,
          deliveryFeeStatus: {
            type: defaultStatus,
            commonDeliveryFee: {
              type: defaultStatus,
              commonDeliveryFeeValue: '',
            },
            feePerKM: {
              type: defaultStatus,
              feePerKMValue: '',
            },
          },
          peakHourDeliveryFeeStatus: {
            type: 'disable',
            data: {
              sunday: [],
              monday: [],
              tuesday: [],
              wednesday: [],
              thursday: [],
              friday: [],
              saturday: [],
          },
        },
      },
      },
      disabledDays: initialDisabledDays,
    },
    onSubmit: () => {},
    validate,
  });

  // --- Sync local disabledDays state with Formik ---
  const [disabledDays, setDisabledDays] = useState<Record<DayType, boolean>>(initialDisabledDays);
  useEffect(() => {
    setDisabledDays(formik.values.disabledDays);
  }, [formik.values.disabledDays]);
  // ---

  // Reset internal Formik state when resetTrigger changes
  useEffect(() => {
    if (resetTrigger && resetTrigger > 0) {
      // Reset to default values (same as initialValues)
      formik.setValues({
        deliveryFee: {
          deliveryChargeSetting: {
            type: defaultType,
            deliveryFeeStatus: {
              type: defaultStatus,
              commonDeliveryFee: {
                type: defaultStatus,
                commonDeliveryFeeValue: '',
              },
              feePerKM: {
                type: defaultStatus,
                feePerKMValue: '',
              },
            },
            peakHourDeliveryFeeStatus: {
              type: 'disable',
              data: {
                sunday: [],
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
              },
            },
          },
        },
        disabledDays: {
          Sunday: true,
          Monday: true,
          Tuesday: true,
          Wednesday: true,
          Thursday: true,
          Friday: true,
          Saturday: true,
        },
      });
    }
  }, [resetTrigger]);

  // When editing an existing store, we still ignore any stored custom delivery fee configuration
  // and always enforce default / disabled settings for merchants.
  useEffect(() => {
    if (storeId) {
      formik.setValues({
        deliveryFee: {
          deliveryChargeSetting: {
            type: defaultType,
            deliveryFeeStatus: {
              type: defaultStatus,
              commonDeliveryFee: {
                type: defaultStatus,
                commonDeliveryFeeValue: '',
              },
              feePerKM: {
                type: defaultStatus,
                feePerKMValue: '',
              },
            },
            peakHourDeliveryFeeStatus: {
              type: 'disable',
              data: {
                sunday: [],
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
            },
          },
        },
        },
        disabledDays: {
              Sunday: true,
              Monday: true,
              Tuesday: true,
              Wednesday: true,
              Thursday: true,
              Friday: true,
              Saturday: true,
        },
      });
    }
  }, [storeId]);

  useEffect(() => {
    // Always sync the locked, default delivery fee configuration to the parent form.
    form.setFieldValue(field.name, formik.values.deliveryFee);
  }, [field.name, form, formik.values.deliveryFee]);

  // Automatically switch to default if all days are disabled for peak hour
  useEffect(() => {
    if (
      formik.values.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.type === 'enable'
    ) {
      const allDaysDisabled = Object.values(disabledDays).every(Boolean);
      if (allDaysDisabled) {
        // Don't auto-disable, just show a warning
        // The user can manually disable if they want
      }
    }
  }, [disabledDays, formik]);

  // Allow re-enabling when at least one day is enabled
  useEffect(() => {
    if (
      formik.values.deliveryFee.deliveryChargeSetting.peakHourDeliveryFeeStatus.type === 'disable'
    ) {
      const atLeastOneDayEnabled = Object.values(disabledDays).some(disabled => !disabled);
      if (atLeastOneDayEnabled) {
        // Allow re-enabling Peak Hour Delivery Fee Status
        // Don't auto-enable, but allow manual enabling
      }
    }
  }, [disabledDays, formik]);

  const location = useLocation();
  const navigate = useNavigate();
  const setStoreAtom = useSetAtom(storeAtom);

  // UseEffect to check the presence of `id` in the URL
  useEffect(() => {
    const pathParts = location.pathname.split('/'); // Split the URL by "/"
    const id = pathParts[pathParts.length - 1]; // The last part should be the id

    if (!id) {
      setStoreAtom(null); // Set atom to null if id doesn't exist
    }
  }, [location, setStoreAtom, navigate]);

  return (
    <Paper
      elevation={3}
      sx={{
        border: '1px solid grey',
        padding: '10px',
        position: 'relative',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-17px',
          background: 'white',
          padding: '0 5px',
        }}
      >
        <Typography variant="h6" sx={styles.typography}>
          {t('storeManagement.form.deliveryFee')}
        </Typography>
      </Box>

      {/* Merchants can no longer configure delivery fee settings; inform them that
          platform defaults are applied automatically. */}
      <Box sx={{ mt: 2 }}>
          <Typography sx={styles.typography}>
          {t('storeManagement.form.deliveryChargeSetting')}:{' '}
          {t('storeManagement.form.default')} ({t('settings.disable')})
          </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
                {t('storeManagement.form.peakHourReenableHelp')}
              </Typography>
            </Box>
    </Paper>
  );
};

export default DeliveryFeeForm;
